import { Ref, computed, ref } from 'vue';
import { logger } from '../utils/logger';

/**
 * Упрощенный composable для управления скроллом сообщений
 * Заменяет сложную логику useScrollGlue и дублирующиеся функции в MessagesApp.vue
 */

export interface ScrollOptions {
  /** Тип поведения скролла */
  behavior?: 'smooth' | 'auto';
  /** Принудительный скролл даже если пользователь не внизу */
  force?: boolean;
  /** Ждать загрузки изображений перед скроллом */
  waitForImages?: boolean;
  /** Максимальное время ожидания загрузки изображений (мс) */
  imageTimeout?: number;
  /** Максимальное количество попыток для стабильного скролла (RAF) */
  maxAttempts?: number;
}

export interface MessageScrollState {
  /** Пользователь находится внизу списка */
  isAtBottom: boolean;
  /** Высота прокрутки от низа (пиксели) */
  distanceFromBottom: number;
}

const DEFAULT_THRESHOLD = 300; // Порог для определения "внизу" (как в AngularJS)
const DEFAULT_IMAGE_TIMEOUT = 2000; // Таймаут загрузки изображений
const DEFAULT_MAX_ATTEMPTS = 5; // Максимум попыток для стабильного скролла

export function useMessageScroll(
  containerRef: Ref<HTMLElement | null>,
  onScrollToBottom?: () => void
) {
  const threshold = ref(DEFAULT_THRESHOLD);
  const isScrolling = ref(false);
  const isAtBottom = ref(true); // Local ref for tracking bottom state

  /**
   * Проверить, находится ли пользователь внизу списка
   */
  const checkIsAtBottom = (): boolean => {
    const el = containerRef.value;
    if (!el) return true;
    
    const distance = el.scrollHeight - (el.scrollTop + el.clientHeight);
    return distance <= threshold.value;
  };

  /**
   * Получить текущее состояние скролла
   */
  const getState = (): MessageScrollState => {
    const el = containerRef.value;
    if (!el) {
      return { isAtBottom: true, distanceFromBottom: 0 };
    }
    
    const distance = el.scrollHeight - (el.scrollTop + el.clientHeight);
    return {
      isAtBottom: distance <= threshold.value,
      distanceFromBottom: Math.max(0, distance),
    };
  };

  /**
   * Выполнить скролл вниз
   */
  const scrollToBottom = async (options: ScrollOptions = {}): Promise<void> => {
    const {
      behavior = 'auto',
      force = false,
      waitForImages = false,
      imageTimeout = DEFAULT_IMAGE_TIMEOUT,
    } = options;

    const el = containerRef.value;
    if (!el) {
      logger.warn('useMessageScroll: Container not found');
      return;
    }

    // Проверяем, нужно ли скроллить
    if (!force && !checkIsAtBottom()) {
      logger.log('useMessageScroll: User not at bottom, skipping scroll');
      return;
    }

    isScrolling.value = true;

    try {
      // Ждем загрузки изображений если нужно
      if (waitForImages) {
        await waitForImagesToLoad(el, imageTimeout);
      }

      // Выполняем скролл
      // Используем правильную формулу: scrollHeight - clientHeight
      // Это гарантирует что мы доскроллим до самого низа включая margin-bottom
      const targetScroll = el.scrollHeight - el.clientHeight;
      el.scrollTo({ top: targetScroll, behavior });

      // Вызываем callback
      if (onScrollToBottom) {
        onScrollToBottom();
      }

      logger.log('useMessageScroll: Scrolled to bottom', { force, waitForImages });
    } finally {
      isScrolling.value = false;
    }
  };

  /**
   * Стабильный скролл вниз с проверкой layout через RAF
   * Заменяет каскад setTimeout на рекурсивный requestAnimationFrame
   * Скроллит до тех пор пока scrollHeight стабилизируется
   */
  const scrollToBottomStable = async (options: ScrollOptions = {}): Promise<void> => {
    const {
      behavior = 'auto',
      force = false,
      waitForImages = false,
      imageTimeout = DEFAULT_IMAGE_TIMEOUT,
      maxAttempts = DEFAULT_MAX_ATTEMPTS,
    } = options;

    const el = containerRef.value;
    if (!el) {
      logger.warn('useMessageScroll: Container not found for stable scroll');
      return;
    }

    // Проверяем, нужно ли скроллить
    if (!force && !checkIsAtBottom()) {
      logger.log('useMessageScroll: User not at bottom, skipping stable scroll');
      return;
    }

    isScrolling.value = true;

    try {
      // Ждем загрузки изображений если нужно
      if (waitForImages) {
        await waitForImagesToLoad(el, imageTimeout);
      }

      let attempts = 0;
      let previousHeight = el.scrollHeight;

      const attemptScroll = (): Promise<void> => {
        return new Promise((resolve) => {
          if (attempts >= maxAttempts) {
            logger.log('useMessageScroll: Max attempts reached for stable scroll');
            resolve();
            return;
          }

          attempts++;

          // Выполняем скролл с небольшим дополнительным отступом
          // чтобы гарантированно достичь самого низа (учитываем padding/margin)
          const targetScroll = el.scrollHeight - el.clientHeight + 50; // +50px для гарантии
          el.scrollTo({ top: targetScroll, behavior });

          // Проверяем стабилизацию через RAF
          requestAnimationFrame(() => {
            const currentHeight = el.scrollHeight;

            // Если высота изменилась - продолжаем скроллить
            if (currentHeight !== previousHeight && attempts < maxAttempts) {
              previousHeight = currentHeight;
              resolve(attemptScroll());
            } else {
              // Layout стабилен
              logger.log('useMessageScroll: Stable scroll completed', { 
                attempts, 
                heightChanged: currentHeight !== previousHeight 
              });
              resolve();
            }
          });
        });
      };

      await attemptScroll();

      // Вызываем callback
      if (onScrollToBottom) {
        onScrollToBottom();
      }
    } finally {
      isScrolling.value = false;
    }
  };

  /**
   * Скролл к конкретному элементу по ID сообщения
   */
  const scrollToMessage = (messageId: string | number, behavior: 'smooth' | 'auto' = 'smooth'): boolean => {
    const el = containerRef.value;
    if (!el) return false;

    const messageEl = el.querySelector(`[data-msg-id="${messageId}"]`) as HTMLElement | null;
    if (!messageEl) {
      logger.warn('useMessageScroll: Message element not found', messageId);
      return false;
    }

    messageEl.scrollIntoView({ behavior, block: 'center' });
    return true;
  };

  /**
   * Обработчик события скролла.
   * Вызывает onScrollToBottom ТОЛЬКО на переходе состояния `not-at-bottom` → `at-bottom`,
   * а не на каждом scroll event. Это предотвращает шквал HTTP/WebSocket запросов
   * (например, store.readConversation) при лёгком скролле у дна списка.
   */
  let wasAtBottom = true;
  const handleScroll = (): void => {
    const state = getState();

    // Синхронизируем reactive ref (используется в шаблонах / IntersectionObserver fallback).
    isAtBottom.value = state.isAtBottom;

    // Триггерим callback только на переходе `false → true`, а не на каждом кадре скролла.
    if (state.isAtBottom && !wasAtBottom && onScrollToBottom) {
      onScrollToBottom();
    }
    wasAtBottom = state.isAtBottom;
  };

  // Reactive состояние для использования в шаблонах
  const scrollState = computed<MessageScrollState>(() => getState());

  // IntersectionObserver для более точного отслеживания дна
  let bottomObserver: IntersectionObserver | null = null;
  let bottomAnchor: HTMLElement | null = null;

  /**
   * Настроить IntersectionObserver для отслеживания дна списка
   * Более точный метод чем расчеты scrollHeight
   */
  const setupBottomObserver = (): void => {
    const el = containerRef.value;
    if (!el || bottomObserver) return;

    // Создаем якорный элемент внизу контейнера
    bottomAnchor = document.createElement('div');
    bottomAnchor.className = 'scroll-bottom-anchor';
    bottomAnchor.style.cssText = 'height: 1px; position: absolute; bottom: 0; left: 0; right: 0; pointer-events: none;';
    
    // Добавляем якорь в конец контейнера
    if (el.lastElementChild) {
      el.appendChild(bottomAnchor);
    }

    bottomObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        const nextAtBottom = entry.isIntersecting;
        // Логируем только при смене состояния, а не на каждом кадре скролла.
        if (isAtBottom.value !== nextAtBottom) {
          isAtBottom.value = nextAtBottom;
          logger.debug('useMessageScroll: Bottom observer transition', {
            isAtBottom: nextAtBottom,
            intersectionRatio: entry.intersectionRatio,
          });
        }
      },
      { 
        root: el,
        threshold: 0.5, // Срабатывает когда 50% якоря видимо
        rootMargin: '0px 0px -50px 0px' // Небольшой отступ снизу
      }
    );

    if (bottomAnchor) {
      bottomObserver.observe(bottomAnchor);
    }
  };

  /**
   * Очистить IntersectionObserver
   */
  const cleanupBottomObserver = (): void => {
    if (bottomObserver) {
      bottomObserver.disconnect();
      bottomObserver = null;
    }
    if (bottomAnchor && bottomAnchor.parentNode) {
      bottomAnchor.parentNode.removeChild(bottomAnchor);
      bottomAnchor = null;
    }
  };

  return {
    // Методы
    scrollToBottom,
    scrollToBottomStable,
    scrollToMessage,
    checkIsAtBottom,
    getState,
    handleScroll,
    setupBottomObserver,
    cleanupBottomObserver,
    
    // Состояние
    isScrolling: computed(() => isScrolling.value),
    isAtBottom: isAtBottom,
    distanceFromBottom: computed(() => scrollState.value.distanceFromBottom),
    threshold: computed(() => threshold.value),
  };
}

/**
 * Ожидание загрузки изображений в контейнере
 */
function waitForImagesToLoad(container: HTMLElement, timeout: number): Promise<void> {
  const images = container.querySelectorAll('img');
  const pendingImages = Array.from(images).filter(img => !img.complete);

  if (pendingImages.length === 0) {
    logger.log('useMessageScroll: All images already loaded');
    return Promise.resolve();
  }

  logger.log('useMessageScroll: Waiting for', pendingImages.length, 'images to load');

  return Promise.race([
    // Ожидание загрузки всех изображений
    Promise.all(
      pendingImages.map(
        img =>
          new Promise<void>((resolve) => {
            const onLoad = () => {
              cleanup();
              resolve();
            };

            const onError = () => {
              cleanup();
              resolve(); // Игнорируем ошибки загрузки
            };

            const cleanup = () => {
              img.removeEventListener('load', onLoad);
              img.removeEventListener('error', onError);
            };

            if (img.complete) {
              resolve();
            } else {
              img.addEventListener('load', onLoad);
              img.addEventListener('error', onError);
            }
          })
      )
    ).then(() => undefined),

    // Таймаут
    new Promise<void>((resolve) => {
      setTimeout(() => {
        logger.warn('useMessageScroll: Image loading timeout');
        resolve();
      }, timeout);
    }),
  ]);
}

export default useMessageScroll;
